import {
  focusAndScrollReferenceTarget,
  getResultListItemByResultId,
  getResultReferenceTargetByResultId,
  RESULT_REFERENCE_TARGETS,
  scrollResultListItemIntoView,
} from '@Components/Results/resultListIds';

const handlers = new Map();
const waiters = new Map();
const REGISTRATION_TIMEOUT_MS = 5000;

const buildKey = ({ resultName, reactionId }) => `${resultName}:${reactionId}`;

function findReactionResultListItem(reactionId) {
  const resultListItem = getResultListItemByResultId(reactionId);
  if (resultListItem) {
    return resultListItem;
  }

  const reactionElement = Array.from(document.querySelectorAll('[data-summary-reaction-id]')).find(
    (element) => element.getAttribute('data-summary-reaction-id') === String(reactionId)
  );
  return reactionElement?.closest('.e2e-results-list-item') || reactionElement || null;
}

function waitForHandler(key) {
  const existing = handlers.get(key);
  if (existing) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve, reject) => {
    const waiter = { resolve, reject, timeoutId: null };
    const timeoutId = setTimeout(() => {
      const keyWaiters = waiters.get(key);
      keyWaiters?.delete(waiter);
      if (!keyWaiters?.size) {
        waiters.delete(key);
      }
      reject(new Error('Referenced reaction details did not become available.'));
    }, REGISTRATION_TIMEOUT_MS);
    waiter.timeoutId = timeoutId;

    const keyWaiters = waiters.get(key) || new Set();
    keyWaiters.add(waiter);
    waiters.set(key, keyWaiters);
  });
}

export function registerReactionCitationHandler({ resultName, reactionId }, handler) {
  const key = buildKey({ resultName, reactionId });
  handlers.set(key, handler);
  const keyWaiters = waiters.get(key);
  if (keyWaiters) {
    waiters.delete(key);
    keyWaiters.forEach((waiter) => {
      clearTimeout(waiter.timeoutId);
      waiter.resolve(handler);
    });
  }

  return () => {
    if (handlers.get(key) === handler) {
      handlers.delete(key);
    }
  };
}

export async function revealReactionCondition({ resultName, reactionId, conditionId }) {
  scrollResultListItemIntoView(findReactionResultListItem(reactionId));

  const handler = await waitForHandler(buildKey({ resultName, reactionId }));
  return handler({ conditionId });
}

export async function revealReaction({ resultName, reactionId }) {
  const renderedTarget = getResultReferenceTargetByResultId(reactionId, RESULT_REFERENCE_TARGETS.REACTION);
  if (renderedTarget) {
    focusAndScrollReferenceTarget(renderedTarget);
    return renderedTarget;
  }

  const resultListItem = findReactionResultListItem(reactionId);
  if (!scrollResultListItemIntoView(resultListItem)) {
    throw new Error('Referenced reaction did not become available.');
  }

  // Reaction results are virtualized. Registration happens after the live reaction content
  // replaces its placeholder, so it is a reliable signal that the inner focus target exists.
  await waitForHandler(buildKey({ resultName, reactionId }));
  const reactionTarget = getResultReferenceTargetByResultId(reactionId, RESULT_REFERENCE_TARGETS.REACTION);
  if (!focusAndScrollReferenceTarget(reactionTarget)) {
    throw new Error('Referenced reaction did not become available.');
  }

  return reactionTarget;
}

export function resetReactionCitationCoordinator() {
  handlers.clear();
  waiters.forEach((keyWaiters) => {
    keyWaiters.forEach((waiter) => {
      clearTimeout(waiter.timeoutId);
      waiter.reject(new Error('Reaction citation navigation was reset.'));
    });
  });
  waiters.clear();
}
